import { logTaggedMessage } from '../reporter';
import { Deferred, stringify } from '../../utils';
import { isNextActionChallenge, isNextActionFingerprint, isNextActionRedirect, isNextActionRetry, isNextActionV1Fallback, } from './3ds-utils';
import { performChallenge as adyenChallenge } from './adyen/adyen-challenge';
import { performFingerprint as adyenFingerprint } from './adyen/adyen-fingerprint';
import { performV1Fallback as adyenV1Fallback } from './adyen/adyen-v1fallback';
import { collectBrowserInfo } from './collectBrowserInfo';
import { performChallenge as worldpayChallenge } from './worldpay/worldpay-challenge';
import { performFingerprint as worldpayFingerprint } from './worldpay/worldpay-fingerprint';
import { performV1Fallback as worldpayV1Fallback } from './worldpay/worldpay-v1fallback';
import { redirectAction } from './redirect/redirect-action';
import { setCustomErrorType } from '../custom-error-utils';
import { createVisibleIframe, createHiddenIframe, postToIframe, updateIframeSourceUsingGetRequest, } from './iframe-handler';
const HIDDEN_TIMEOUT_MS = 30000;
const VISIBLE_TIMEOUT_MS = 10 * 60 * 1000; // 10 min
const log = logTaggedMessage({ module: '3ds' });
export const performAuthenticationSteps = async (callServerSubmit) => {
    let res;
    let lastCalledProvider;
    res = await performInitialSubmit(callServerSubmit, lastCalledProvider);
    while (res.threeDsNextAction || res.nextAction?.threeDsNextAction) {
        log(`3ds: received nextAction`, { 'received server response': res });
        const threeDsNextAction = (res.threeDsNextAction ||
            res.nextAction?.threeDsNextAction);
        if (isNextActionRetry(threeDsNextAction)) {
            // RETRY
            const data = threeDsNextAction.retry;
            res = await performRetrySubmit(data, callServerSubmit);
            //
        }
        else if (isNextActionFingerprint(threeDsNextAction)) {
            // FINGERPRINT
            const data = threeDsNextAction.deviceFingerprint;
            lastCalledProvider = data.provider;
            const performFingerprint = getProviderStrategy(data.provider.providerType)
                .fingerprint;
            const threeDsData = await performFingerprint(data);
            log(`3ds: calling server submit after fingerprint`, {
                threeDsData,
            });
            res = await callServerSubmit(threeDsData);
            //
        }
        else if (isNextActionChallenge(threeDsNextAction)) {
            // CHALLENGE
            const data = threeDsNextAction.challenge;
            lastCalledProvider = data.provider;
            const performChallenge = getProviderStrategy(data.provider.providerType)
                .challenge;
            const threeDsData = await performChallenge(data);
            log(`3ds: calling server submit after Challenge`, {
                threeDsData,
            });
            res = await callServerSubmit(threeDsData);
            //
        }
        else if (isNextActionV1Fallback(threeDsNextAction)) {
            // V1Fallback
            const data = threeDsNextAction.v1Fallback;
            lastCalledProvider = data.provider;
            const performV1Fallback = getProviderStrategy(data.provider.providerType)
                .fallback;
            const threeDsData = await performV1Fallback(data);
            log(`3ds: calling server submit after v1fallback`, {
                threeDsData,
            });
            res = await callServerSubmit(threeDsData);
            //
        }
        else if (isNextActionRedirect(threeDsNextAction)) {
            // Redirect (via payments)
            const data = threeDsNextAction.redirect;
            lastCalledProvider = data.provider;
            const threeDsData = await redirectAction(data);
            log(`3ds: calling server submit redirect action (payments)`, {
                threeDsData,
            });
            res = await callServerSubmit(threeDsData);
            //
        }
        else {
            throw Error(`unexpected serverResponse.threeDsNextAction ${stringify(threeDsNextAction)}`);
        }
    }
    return res;
};
const getProviderStrategy = (providerType) => {
    switch (providerType) {
        case 'ADYEN':
            return {
                fingerprint: adyenFingerprint,
                challenge: adyenChallenge,
                fallback: adyenV1Fallback,
            };
        case 'WORLDPAY':
            return {
                fingerprint: worldpayFingerprint,
                challenge: worldpayChallenge,
                fallback: worldpayV1Fallback,
            };
        default:
            return {
                fingerprint: worldpayFingerprint,
                challenge: worldpayChallenge,
                fallback: worldpayV1Fallback,
            };
    }
};
export class ThreeDsTimeoutException extends Error {
    constructor(timeout) {
        const message = `ERR314: a timeout of ${timeout}ms was reached while waiting for 3d-secure result`;
        super(message);
        this.name = 'ThreeDsTimeoutException';
        this.message = message;
        setCustomErrorType(this, ThreeDsTimeoutException);
    }
}
export class ThreeDsFormattingException extends Error {
    constructor(message) {
        super(message);
        this.name = 'ThreeDsFormattingException';
        this.message = message;
        setCustomErrorType(this, ThreeDsTimeoutException);
    }
}
export const listenTo3dsResult = ({ timeout, fromWindow, cancel, originToValidate, }) => {
    const listenPromise = listenFromWindow(fromWindow, originToValidate).then((msg) => msg.data);
    const { timeoutPromise, clearTimeout } = rejectOnTimeout(timeout);
    const promises = [timeoutPromise, listenPromise];
    if (typeof cancel !== 'undefined') {
        promises.push(cancel);
    }
    return Promise.race(promises).finally(() => {
        clearTimeout();
    });
};
export const performInitialSubmit = (callServerSubmit, failedProvider) => {
    const threeDsData = {
        browserInfo: collectBrowserInfo(),
        challengeDisplaySize: getThreeDsChallengeDisplaySize(),
        provider: undefined,
        failedProvider,
    };
    return callServerSubmit({
        initialData: threeDsData,
    });
};
export const performRetrySubmit = ({ provider }, callServerSubmit) => {
    const threeDsData = {
        browserInfo: collectBrowserInfo(),
        challengeDisplaySize: getThreeDsChallengeDisplaySize(),
        provider,
        failedProvider: undefined,
    };
    log(`3ds: calling server submit for Retry at ${provider.providerType}`, {
        threeDsData,
    });
    return callServerSubmit({
        initialData: threeDsData,
    });
};
export const getThreeDsChallengeDisplaySize = () => {
    return 'SMALL';
};
const rejectOnTimeout = (ms) => {
    let timerId;
    const timeoutPromise = new Promise((_resolve, reject) => {
        timerId = setTimeout(() => {
            return reject(new ThreeDsTimeoutException(ms));
        }, ms);
    });
    return {
        timeoutPromise,
        clearTimeout: () => {
            timerId && clearTimeout(timerId);
        },
    };
};
const listenFromWindow = (fromWindow, originToValidate) => {
    return new Promise((resolve) => {
        window.addEventListener('message', (e) => {
            log(`3ds: message recived back from iframe`, {
                origin: e.origin,
                data: e?.data,
                originToValidate,
            });
            const isSameSource = e.source === fromWindow;
            const isSameOrigin = originToValidate
                ? e.origin === originToValidate
                : true;
            if (isSameSource && isSameOrigin) {
                log(`3ds: message source from 3ds ifrmae confirmed `, {
                    origin: e.origin,
                    data: e?.data,
                });
                resolve(e);
            }
        }, false);
    });
};
export const renderIframeAndWaitForPostback = (type, methodUrl, iframeMethodType, requestRawPostData, originToValidate) => {
    const cancelByUser = new Deferred();
    const iframeName = type === 'hidden'
        ? createHiddenIframe()
        : createVisibleIframe(cancelByUser);
    const timeout = process.env.NODE_ENV !== 'production' && window.override3dsTimeout
        ? window.override3dsTimeout
        : type === 'hidden'
            ? HIDDEN_TIMEOUT_MS
            : VISIBLE_TIMEOUT_MS;
    log(`3ds: rendering ${type} iframe, listening for postMessage from it`, {
        methodUrl,
        requestRawPostData,
        iframeName,
        timeout,
    });
    const resultPromise = listenTo3dsResult({
        timeout,
        fromWindow: window.frames[iframeName],
        cancel: cancelByUser.promise,
        originToValidate,
    });
    if (iframeMethodType === 'GET') {
        updateIframeSourceUsingGetRequest({
            url: methodUrl,
            iframeName,
        });
    }
    else {
        postToIframe({
            url: methodUrl,
            fields: requestRawPostData,
            iframeName,
        });
    }
    return resultPromise;
};
//# sourceMappingURL=3d-secure.js.map